/**
 * Which product names a project's bug query should cover.
 *
 * A project created from a managed product is about a *device* — an i4600, a
 * Virtual Edition — and a device runs many modules. Its bug list is therefore
 * the union across that device's whole module surface, not the single module
 * that happens to sit in `productId`.
 *
 * CVEs need no equivalent: they are queried by software type (the product
 * family, e.g. "BIG-IP") and already span the device. That asymmetry is why a
 * version shows hundreds of CVEs and tens of bugs.
 *
 * Vendor-neutral: the module list comes from the managed product's own
 * `vendorData.vendorBugModules`, which every vendor populates.
 */

const moduleNames = managedProduct => {
  const modules = managedProduct?.vendorData?.vendorBugModules;
  if (!Array.isArray(modules)) return [];
  return modules
    .filter(m => m && !m.disabled)
    .map(m => m.value || m.label)
    .filter(name => typeof name === 'string' && name.trim());
};

/**
 * @param {object} project        the Plan project
 * @param {Array}  managedProducts the tenant's managed products for this vendor
 * @param {string} fallbackName   the single product name used today
 * @returns {string[]} names to query, never empty when fallbackName is given
 */
export function projectProductNames(project, managedProducts, fallbackName) {
  const single = [fallbackName].filter(Boolean);
  if (!project?.managedProductId) return single;

  const managedProduct = (managedProducts || []).find(mp => mp.id === project.managedProductId);
  const names = moduleNames(managedProduct);
  // A managed product that reports no modules is not evidence that the device
  // runs none — it means we have no module data. Falling back to the single
  // name keeps the project showing what it always showed rather than emptying
  // its bug list.
  if (!names.length) return single;

  // The project's own module stays in the set even if the managed product no
  // longer lists it, so a project never loses the issues it was created to
  // review.
  return fallbackName && !names.includes(fallbackName) ? [fallbackName, ...names] : names;
}
